import { currentLanguage, hasTranslation, t, tIn } from '@/lib/i18n'
import type { BandStatus, CandidateSectionStatus, CandidateStatus, GradingStatus, SectionScoreStatus, TestResult } from '@/lib/types'
import { isShowableBand } from './band-display'
import { NOT_ATTEMPTED_ERROR_CODE } from './candidate-view'
import { resolveGradingStatus } from './grading-status'
import { INDICATIVE_LINE, REVIEWED_LINE } from './review-wording'

/**
 * What a student is told about a result, from the fields the server already sends (DECISIONS 11 and 12,
 * PAGES.md sections 2.2 and 2.3). Pure functions, no React, no fetching.
 *
 * Owner rule 1 (17 Oct 2026, grading robustness): a student NEVER sees Band 0, "failed" or "unassessable". So there are
 * exactly these states:
 *   Published (server `published` or `confirmed`), with a green "Reviewed by a qualified teacher" pill when a teacher
 *   confirmed the band; Indicative (server `indicative`); Processing (every real attempt that is not marked yet:
 *   pending, running, needs_review, failed, unassessable-with-text, retrying, or an HTTP 202); Not attempted (the student
 *   submitted nothing to mark: `result_not_attempted`); plus ended (abandoned) and untimed practice.
 * Internal failure states are folded into Processing here, whatever the server sends, so an API that has not deployed
 * its own `candidate_status` yet cannot leak them. "Not finished", "Not published" and "Ready to review" do not exist for
 * students.
 *
 * Admin surfaces keep `grading-status.ts` and `review-model.ts` labels; this module does not replace them.
 */

export type SkillKey = 'listening' | 'reading' | 'writing' | 'speaking'

/**
 * How long a not-yet-marked attempt may sit before the page adds the calm "taking longer" line. Marking normally finishes
 * in minutes; the automatic retry after a failure lands 10 to 15 minutes in, so the line waits until a retry has had time.
 */
export const DELAYED_AFTER_MS = 30 * 60 * 1000


export interface StatusSectionInput {
  band?: number | null
  score?: number | null
  max_score?: number | null
  score_status?: SectionScoreStatus | null
  examiner_reviewed_at?: string | null
  /** True only while an open review still waits for a teacher. False for a declined, dismissed or superseded review, which stays `indicative` for good. */
  review_pending?: boolean
  not_attempted?: boolean
  not_attempted_reason?: string
  assessment_status?: GradingStatus
  grading_status?: GradingStatus
  /** The server's student-safe state of this section, when it sends one. */
  candidate_status?: CandidateSectionStatus | null
}

export interface StudentStatusInput {
  attemptStatus?: 'completed' | 'abandoned' | null
  timingMode?: 'timed' | 'untimed' | null
  /** The grading lifecycle as the page resolved it (`resolveGradingStatus` for a list row, review rules for a detail). */
  lifecycle: GradingStatus
  /** `score_publication_status === 'published'`. */
  published: boolean
  bandStatus?: BandStatus | null
  overallBand?: number | null
  sections?: Partial<Record<SkillKey, StatusSectionInput | null>>
  /** `grading_queued_at`, or `completed_at` when the server did not send it. */
  queuedAt?: string | null
  /** The review endpoint answered 202 (pending, running, needs_review). */
  pendingResponse?: boolean
  /** The server's student-safe state of the whole result, when it sends one (it wins for `not_attempted`, `ended` and `untimed`). */
  candidateStatus?: CandidateStatus | null
  /** `grading_error_code` / `error_code`. Read only to tell "nothing was submitted" from "not marked yet"; never shown. */
  errorCode?: string | null
  /** Injected clock for tests; defaults to `Date.now()`. */
  now?: number
}

export type StudentResultState = 'ended' | 'untimed' | 'not-attempted' | 'being-marked' | 'indicative' | 'published'
/**
 * `being-marked` is drawn as "Processing". `could-not-mark` is NOT a student state: `studentResultStatus` never returns it.
 * It stays here only for the teacher-facing school pages, which show a class's results to a teacher.
 */
export type StatusPillState = 'published' | 'indicative' | 'being-marked' | 'could-not-mark' | 'not-attempted' | 'untimed'

export interface StudentResultStatus {
  state: StudentResultState
  /** The pill to draw, or null when there is none (an ended attempt, or an old row with no band). */
  pill: StatusPillState | null
  /** A teacher confirmed the band (row 7). Draw the green ReviewedPill. */
  reviewed: boolean
  /** Indicative, and a Writing or Speaking band still waits for a teacher (the server's `review_pending`). The only case the Indicative line may say a teacher can still adjust it. */
  reviewPending: boolean
  /** The latest teacher confirmation among Writing and Speaking, when known. */
  reviewedAt: string | null
  /** Not marked yet for thirty minutes or more: add the calm "taking longer than usual" sub-line. */
  delayed: boolean
  /** The overall band to show, or null (never 0, never NaN). Null means "No band yet", a skeleton or the panel. */
  band: number | null
}

const SKILLS: readonly SkillKey[] = ['listening', 'reading', 'writing', 'speaking']

function normaliseLifecycle(lifecycle: GradingStatus): Exclude<GradingStatus, 'completed'> {
  return lifecycle === 'completed' ? 'succeeded' : lifecycle
}

function showable(value: number | null | undefined): number | null {
  return isShowableBand(value) ? value : null
}

/**
 * Latest `examiner_reviewed_at` among Writing and Speaking sections whose `score_status` is `confirmed`.
 * The server also stamps `examiner_reviewed_at` on declined reviews (they stay Indicative), so the date alone
 * never counts as a review.
 */
function latestConfirmedReview(sections: StudentStatusInput['sections']): string | null {
  let latest: { at: string; time: number } | null = null
  for (const skill of ['writing', 'speaking'] as const) {
    const section = sections?.[skill]
    if (!section || section.score_status !== 'confirmed' || !section.examiner_reviewed_at) continue
    const time = Date.parse(section.examiner_reviewed_at)
    if (Number.isNaN(time)) continue
    if (!latest || time > latest.time) latest = { at: section.examiner_reviewed_at, time }
  }
  return latest?.at ?? null
}

/**
 * A Writing or Speaking band that still waits for a teacher: the server's own `review_pending`, true only while a review is open.
 * A declined, dismissed or superseded review stays Indicative with this false, and no teacher will look at it again.
 */
function anyReviewPending(sections: StudentStatusInput['sections']): boolean {
  return (['writing', 'speaking'] as const).some((skill) => sections?.[skill]?.review_pending === true)
}

/** Result-level status, PAGES.md 2.2. The first matching row wins. */
export function studentResultStatus(input: StudentStatusInput): StudentResultStatus {
  const lifecycle = normaliseLifecycle(input.lifecycle)
  const base = { reviewed: false, reviewPending: false, reviewedAt: null, delayed: false, band: null } as const

  // 1. Abandoned: not listed; a deep link shows the "ended early" panel and no pill.
  if (input.attemptStatus === 'abandoned' || input.candidateStatus === 'ended') return { ...base, state: 'ended', pill: null }
  // 2. Untimed practice never gets a band.
  if (input.timingMode === 'untimed' || input.candidateStatus === 'untimed') return { ...base, state: 'untimed', pill: 'untimed' }
  // 3. The student submitted nothing to mark: said in words ("Not attempted"), never as a number and never as a failure.
  if (input.candidateStatus === 'not_attempted' || (lifecycle === 'unassessable' && input.errorCode === NOT_ATTEMPTED_ERROR_CODE)) {
    return { ...base, state: 'not-attempted', pill: 'not-attempted' }
  }
  // 4. A real attempt that is not marked yet. A failed or unassessable grading is retried automatically, so to the student
  //    it is a wait ("Processing"), exactly like pending, running and a held Speaking review, or an HTTP 202.
  if (
    input.candidateStatus === 'processing'
    || lifecycle === 'pending' || lifecycle === 'running' || lifecycle === 'needs_review' || lifecycle === 'processing'
    || lifecycle === 'failed' || lifecycle === 'unassessable'
    || input.pendingResponse
  ) {
    const queued = input.queuedAt ? Date.parse(input.queuedAt) : Number.NaN
    const now = input.now ?? Date.now()
    // A held examiner review is expected to take longer than a marking run, so it never reads "taking longer than usual".
    const delayed = lifecycle !== 'needs_review' && !Number.isNaN(queued) && now - queued >= DELAYED_AFTER_MS
    return { ...base, state: 'being-marked', pill: 'being-marked', delayed }
  }
  const band = showable(input.overallBand)
  if (lifecycle === 'succeeded' && input.published) {
    // 6. Indicative.
    if (input.bandStatus === 'indicative') return { ...base, state: 'indicative', pill: 'indicative', band, reviewPending: anyReviewPending(input.sections) }
    // 7. A teacher confirmed every Writing and Speaking band.
    if (input.bandStatus === 'confirmed') {
      return { ...base, state: 'published', pill: 'published', reviewed: true, reviewedAt: latestConfirmedReview(input.sections), band }
    }
    // 8. Published, no examiner involved (or an older API that sends no label).
    return { ...base, state: 'published', pill: 'published', band }
  }
  // 9. Anything else (succeeded but never published on an old API, unknown values): no pill, no band.
  return { ...base, state: 'published', pill: null }
}

type WithSections = Partial<Record<SkillKey, StatusSectionInput | null>>

function sectionsOf(scores: unknown): WithSections {
  const out: WithSections = {}
  if (!scores || typeof scores !== 'object') return out
  for (const skill of SKILLS) {
    const value = (scores as Record<string, unknown>)[skill]
    if (value && typeof value === 'object') out[skill] = value as StatusSectionInput
  }
  return out
}

/** Status input for a row of `GET /tests/results/`. */
export function studentStatusInputFromResult(result: TestResult, now?: number): StudentStatusInput {
  let lifecycle = normaliseLifecycle(resolveGradingStatus(result))
  // The review endpoint maps "succeeded but withheld" to needs_review; a list row says it in publication only.
  if (lifecycle === 'succeeded' && result.score_publication_status === 'withheld') lifecycle = 'needs_review'
  return {
    attemptStatus: result.status ?? null,
    timingMode: result.timing_mode ?? null,
    lifecycle,
    published: result.score_publication_status === 'published',
    bandStatus: result.band_status ?? null,
    overallBand: result.section_scores?.overall_band ?? null,
    sections: sectionsOf(result.section_scores),
    queuedAt: result.completed_at ?? null,
    candidateStatus: result.candidate_status ?? null,
    errorCode: result.grading_error_code ?? null,
    now,
  }
}

/** The slice of `TestReviewData` the status reads, so the domain does not import a feature type. */
export interface ReviewStatusSource {
  grading_status?: GradingStatus
  assessment_status?: GradingStatus
  score_publication_status?: string | null
  result_summary: {
    status?: 'completed' | 'abandoned'
    grading_status?: GradingStatus
    candidate_status?: CandidateStatus
    error_code?: string | null
    score_publication_status?: string | null
    band_status?: BandStatus | null
    timing_mode?: 'timed' | 'untimed'
    grading_queued_at?: string | null
    completed_at?: string
    section_scores: object
  }
}

/** Status input for `GET /tests/results/<id>/review/`. `pendingResponse` is true when the server answered 202. */
export function studentStatusInputFromReview(data: ReviewStatusSource, options: { pendingResponse?: boolean; now?: number } = {}): StudentStatusInput {
  const summary = data.result_summary
  const publication = summary.score_publication_status || data.score_publication_status || null
  let lifecycle = normaliseLifecycle(summary.grading_status || data.grading_status || data.assessment_status || 'pending')
  if (lifecycle === 'succeeded' && publication === 'withheld') lifecycle = 'needs_review'
  const scores = summary.section_scores as Record<string, unknown>
  const overall = scores.overall_band
  return {
    attemptStatus: summary.status ?? null,
    timingMode: summary.timing_mode ?? null,
    lifecycle,
    published: publication === 'published',
    bandStatus: summary.band_status ?? null,
    overallBand: typeof overall === 'number' ? overall : null,
    sections: sectionsOf(scores),
    queuedAt: summary.grading_queued_at || summary.completed_at || null,
    pendingResponse: options.pendingResponse,
    candidateStatus: summary.candidate_status ?? null,
    errorCode: summary.error_code ?? null,
    now: options.now,
  }
}

/** The one line under a result header (PAGES.md 2.2 "Header status line"), translated, or null when there is none. */
export function studentStatusLine(status: StudentResultStatus): string | null {
  switch (status.state) {
    case 'ended':
      return null
    case 'untimed':
      return t('Untimed practice does not get a band.')
    case 'not-attempted':
      return t('No answers were submitted for this attempt.')
    case 'being-marked':
      return t('Your result is processing. It usually appears within minutes.')
    case 'indicative':
      return reviewLine(status)
    case 'published':
      if (status.pill === null) return null
      return reviewLine(status) ?? tIn('result', 'Published')
  }
}

/**
 * Where a band stands with a teacher, in the one wording (`review-wording.ts`): Indicative while a teacher still owes a decision,
 * "Published after teacher review" once one has made it, null for a band no teacher was ever asked about. An Indicative band whose
 * review closed without a decision (declined, dismissed, superseded) gets no line: it never promises a check that will not happen.
 * Reads the server's own label as `studentResultStatus` and `studentSectionStatus` resolved it, so it takes either.
 */
export function reviewLine(status: { state: string; reviewed: boolean; reviewPending: boolean }): string | null {
  if (status.state === 'indicative') return status.reviewPending ? t(INDICATIVE_LINE) : null
  return status.reviewed ? t(REVIEWED_LINE) : null
}

/* -------------------------------------------------------------------------- */
/* Section level (skill tiles and skill tabs), PAGES.md 2.3                    */
/* -------------------------------------------------------------------------- */

export type SectionState =
  | 'ended'
  | 'not-attempted'
  | 'indicative'
  | 'published'
  | 'marked-early'
  | 'being-marked'
  | 'no-band'

export interface SectionStatus {
  state: SectionState
  /** The section band to show, or null (never 0). */
  band: number | null
  /** Writing or Speaking band a teacher confirmed (draw the small "Teacher reviewed" pill). */
  reviewed: boolean
  /** Indicative, and an open review still waits for a teacher (the server's `review_pending`). */
  reviewPending: boolean
  reviewedAt: string | null
  /** Listening or Reading with a raw score but no band ("32 of 40 correct" plus "No band yet"). */
  raw: { score: number; max: number } | null
  /** The server's candidate-safe sentence for `not_attempted`, when present. */
  notAttemptedReason: string | null
}

/**
 * Section-level status. `result` is the result-level status of the same attempt.
 *
 * Deviation from the PAGES.md table, flagged: row 7 reads "showable band and result state published". A
 * Listening or Reading band (which has no `score_status`) inside an Indicative overall result would then fall to
 * "No band yet" and hide a real, final band. Here row 7 applies to every result state except being-marked
 * (row 8, "Final when the whole test is marked") and ended.
 *
 * Owner rule 1: a section whose grading failed or could not be assessed is "Processing" (it is retried automatically), a
 * section the student left blank is "Not attempted", and a stored band of 0 (a legacy published value) is never drawn:
 * with no recorded score it reads "Not attempted", the IELTS meaning of a 0.
 */
export function studentSectionStatus(
  score: StatusSectionInput | null | undefined,
  result: Pick<StudentResultStatus, 'state'>,
  skill: SkillKey,
): SectionStatus {
  const base: SectionStatus = { state: 'no-band', band: null, reviewed: false, reviewPending: false, reviewedAt: null, raw: null, notAttemptedReason: null }
  const band = showable(score?.band)
  const hasExaminerFields = skill === 'writing' || skill === 'speaking'

  // 1
  if (result.state === 'ended') return { ...base, state: 'ended' }
  // 2
  if (score?.not_attempted === true || score?.candidate_status === 'not_attempted' || result.state === 'not-attempted') {
    return { ...base, state: 'not-attempted', notAttemptedReason: score?.not_attempted_reason?.trim() || null }
  }
  // 3 and 4: a failed, unassessable or processing section is a wait, never an error the student has to read.
  const sectionLifecycle = score?.assessment_status ?? score?.grading_status
  if (band === null && (sectionLifecycle === 'failed' || sectionLifecycle === 'unassessable' || sectionLifecycle === 'processing' || score?.candidate_status === 'processing')) {
    return { ...base, state: 'being-marked' }
  }
  if (band !== null) {
    // 5
    if (score?.score_status === 'indicative') return { ...base, state: 'indicative', band, reviewPending: score.review_pending === true }
    // 6
    if (score?.score_status === 'confirmed') {
      return {
        ...base,
        state: 'published',
        band,
        reviewed: hasExaminerFields,
        reviewedAt: hasExaminerFields ? score.examiner_reviewed_at ?? null : null,
      }
    }
    // 8 (before 7, see the doc comment)
    if (result.state === 'being-marked') return { ...base, state: 'marked-early', band }
    // 7
    return { ...base, state: 'published', band }
  }
  // 9: a stored band that is not a band (a legacy 0), or a Listening or Reading section with no band and no points. Never
  // printed as a number. No recorded points means nothing was answered, the IELTS meaning of a 0, so it reads "Not attempted";
  // points with a 0 band is an old inconsistent row, shown as the raw score with no band.
  const zeroBand = typeof score?.band === 'number' && score.band <= 0
  const objectiveNoPoints = (skill === 'listening' || skill === 'reading') && (score?.band === null || score?.band === undefined) && score?.score === 0
  if (score && (zeroBand || objectiveNoPoints) && (result.state === 'published' || result.state === 'indicative')) {
    if (isFiniteNumber(score.score) && isFiniteNumber(score.max_score) && score.max_score > 0 && score.score > 0) {
      return { ...base, state: 'no-band', raw: { score: score.score, max: score.max_score } }
    }
    return { ...base, state: 'not-attempted', notAttemptedReason: score.not_attempted_reason?.trim() || null }
  }
  // 10
  if (result.state === 'being-marked') return { ...base, state: 'being-marked' }
  // 11
  return base
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/**
 * The line under a Writing or Speaking band: that band's own state, never the whole result's (a full mock can have a confirmed
 * Writing band beside an Indicative Speaking one). A teacher review is not repeated here when the page header already says it.
 */
export function sectionReviewLine(section: SectionStatus, result: Pick<StudentResultStatus, 'reviewed'>): string | null {
  if (section.state === 'indicative') return reviewLine(section)
  return section.reviewed && !result.reviewed ? reviewLine(section) : null
}

/** The short headline for an empty band slot: "Not attempted", "Processing", or "No band yet". Never a number. */
export function sectionNoBandHeadline(status: Pick<SectionStatus, 'state'>): string {
  if (status.state === 'not-attempted') return t('Not attempted')
  if (status.state === 'being-marked') return t('Processing')
  return t('No band yet')
}

/** The sentence(s) a tile shows for a section that has no band to draw, translated. Empty when a band is drawn. */
export function sectionStatusLines(status: SectionStatus): string[] {
  switch (status.state) {
    case 'ended':
      return [t('This section was not marked.')]
    case 'not-attempted':
      // The server's candidate-safe sentence replaces the generic one. In Arabic it only does so when the
      // catalogue carries it; untranslated server text is never shown there.
      return [
        t('Not attempted'),
        status.notAttemptedReason && (currentLanguage() !== 'ar' || hasTranslation(status.notAttemptedReason))
          ? t(status.notAttemptedReason)
          : t('You did not answer this section, so it has no band.'),
      ]
    case 'marked-early':
      return [t('Final when the whole test is marked.')]
    case 'being-marked':
      return [t('Processing')]
    case 'no-band':
      return status.raw
        ? [t('{0} of {1} correct', { 0: status.raw.score, 1: status.raw.max }), t('No band yet')]
        : [t('No band yet')]
    case 'indicative':
    case 'published':
      return []
  }
}
